import { FC, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem, TextField, Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Choice, Status, VerifiedStatus } from 'src/enums/common';
import { DateField } from 'src/components/date-field';
import { GetUserProductResponse, UserProduct } from 'src/interfaces/userProduct/user-product';
import { userProductApi } from 'src/api/userProduct';
import { productApi } from 'src/api/products';
import { modelApi } from 'src/api/model';
import { ResponseCodes } from 'src/enums/responseCodes';
import toast from 'react-hot-toast';
import loadingContext from 'src/loderContext';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { MenuProps } from 'src/components/masters/customCss';
import { ProductModel } from 'src/interfaces/masters/base/product';
import { BrandModel } from 'src/interfaces/masters/base/brand';
import { brandApi } from 'src/api/brand';
import { getProductTypeArrayById } from 'src/components/masters/customFunctions';
import { InputField } from 'src/components/input-field';

interface AddProductProps {
  userId?: string;
  open: boolean;
  onClose: () => void;
  setProductState: (data: GetUserProductResponse) => void;
}
const choice = Object.values(Choice);

export const AddProduct: FC<AddProductProps> = (props) => {
  const { open, onClose, userId, setProductState } = props;
  const [products, setProducts] = useState<ProductModel[]>();
  const [brands, setBrands] = useState<BrandModel[]>();
  const [modelNumbers, setModelNumbers] = useState<string[]>([]);
  const { setLoading } = useContext(loadingContext);

  const getProducts = async () => {
    const paginationObject: PaginationAndFilter = {
      filters: [{ property: 'status', value: [Status.PUBLISH], operator: 'in' }],
      pageSize: 1000, // keeping it max to 1000
      pageNumber: 1,
      sort: 'desc',
      sortBy: 'lastUpdate'
    };
    const result = await productApi.getProductsByPaginationAndFilters(paginationObject);
    setProducts(result.responseData.records);
  };

  const getBrands = async () => {
    const paginationObject: PaginationAndFilter = {
      filters: [{ property: 'status', value: [Status.PUBLISH], operator: 'in' }],
      pageSize: 1000, // keeping it max to 1000
      pageNumber: 1,
      sort: 'desc',
      sortBy: 'lastUpdate'
    };
    const result = await brandApi.getBrandsByPaginationAndFilters(paginationObject);
    setBrands(result.responseData.records);
  };

  const textRegex = /^[A-Za-z0-9]+$/;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      nickName: '',
      productId: '',
      productType: '',
      brandId: '',
      modelNo: '',
      serialNo: '',
      purchaseDate: null,
      submit: null
    },
    validationSchema: Yup.object().shape({
      nickName: Yup.string().matches(new RegExp(/^[A-Za-z ]+$/),
        'Only alphabets and spaces allowed').optional(),
      productId: Yup.string().required('Required'),
      productType: Yup.string().required('Required'),
      brandId: Yup.string().required('Required'),
      serialNo: Yup.string().matches(new RegExp(textRegex), 'Only numbers and strings are allowed').optional(),
      warrantyStatus: Yup.string().oneOf(choice, 'Select from the list'),
      purchaseDate: Yup.date()
        .when('warrantyStatus', {
          is: Choice.YES,
          then: Yup.date().required()
            .typeError('Date must be in DD/MM/YYYY format')
            .min(new Date('01/01/2000'), 'Date should be greater than 01/01/2000')
            .max(new Date(), 'Date should be less than or equal to todays date'),
          otherwise: Yup.date().nullable().notRequired()
        }).optional()
    }),

    onSubmit: async (values, helpers) => {
      setLoading(true);
      const addProduct: UserProduct = {
        id: '',
        nickName: formik.values.nickName,
        productId: formik.values.productId,
        productType: formik.values.productType,
        brandId: formik.values.brandId,
        serialNo: values.serialNo,
        modelNo: values.modelNo?.trim(),
        purchaseDate: values.purchaseDate || null,
        additionalDocuments: [],
        userId,
        status: VerifiedStatus.ACTIVE,
        isEditable: true,
        warrantyStatus: false
      };
      const result = await userProductApi.addUserProduct(addProduct);
      if (result.statusCode === ResponseCodes.CREATED) {
        setProductState(result?.responseData);
        helpers.resetForm();
        toast.success('Product Created Successfully');
        onClose();
      } else {
        toast.error(result?.responseData?.message?.toString());
      }
      setLoading(false);
    }
  });

  const mandatoryFieldName = (text: string, isError: boolean) => (
    <Typography
      variant="button"
      display="inline-flex"
      flexDirection="row"
      color={isError ? 'red' : 'black'}
    >
      {text}
      {' '}
      <Typography
        sx={{
          ml: '4px'
        }}
        fontSize="15px"
        flexDirection="inherit"
        color="red"
      >
        {' '}
        *
      </Typography>
    </Typography>
  );
  // fetches distinct modelNumbers and update state
  const getModelNumbers = async () => {
    const paginationObject: PaginationAndFilter = {
      filters: [{
        property: 'productId',
        operator: 'equal',
        value: [
          formik.values.productId,
        ],
      },
      {
        property: 'brandId',
        operator: 'equal',
        value: [
          formik.values.brandId,
        ],
      }],
      pageSize: 99999,
      pageNumber: 1,
      sort: -1,
      sortBy: 'lastUpdate'
    };
    // call to the pagination api
    const result = await modelApi.getModelsByPaginationAndFilter(paginationObject);
    if (result.statusCode === ResponseCodes.OK) {
      const allModelNumber = result?.responseData?.records?.map((item) => item.modelNo);
      if (allModelNumber.length) {
        setModelNumbers(allModelNumber);
      }
    } else {
      toast.error('Something Went Wrong');
    }
  };

  useEffect(() => {
    getProducts().catch(console.error);
    getBrands().catch(console.error);
    getModelNumbers().catch(console.error);
  }, []);

  useEffect(() => {
    if (formik.values.brandId && formik.values.productId) {
      getModelNumbers().catch(console.error);
    }
  }, [formik.values.brandId, formik.values.productId]);

  // this method maintain the original values when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      formik.resetForm();
      onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle>
        Add Product
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => closeDialog(e, '')}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <InputField
              error={Boolean(formik.touched.nickName && formik.errors.nickName)}
              helperText={formik.touched.nickName && formik.errors.nickName}
              fullWidth
              label="Product Nick Name"
              name="nickName"
              onChange={(event) => {
                if (event.target.value !== ' ') {
                  formik.setFieldValue('nickName', event.target.value);
                }
              }}
              onBlur={formik.handleBlur}
              placeholder="Enter Nick Name"
              value={formik.values.nickName}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            {mandatoryFieldName('Product', Boolean(formik.touched.productId && formik.errors.productId))}
            <Autocomplete
              freeSolo
              options={products}
              onChange={(event, newValue) => {
                const getItem = newValue as ProductModel;
                formik.setFieldValue('productId', getItem?.id);
                formik.setFieldValue('productType', '');
              }}
              onBlur={formik.handleBlur}
              getOptionLabel={(option) => (option?.name ? option?.name : '')}
              selectOnFocus
              disableClearable
              renderOption={(propss, option) => <li {...propss}>{option?.name}</li>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(formik.touched.productId && formik.errors.productId)}
                  helperText={formik.touched.productId && formik.errors.productId}
                  name="productId"
                  required
                  placeholder="Enter Product"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >

            <InputField
              error={Boolean(formik.touched.productType && formik.errors.productType)}
              fullWidth
              helperText={formik.touched.productType && formik.errors.productType}
              placeholder="Product Type"
              label="Product Type"
              name="productType"
              required
              disabled={formik.values.productId === ''}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              select
              value={formik.values.productType}
              SelectProps={{ MenuProps }}
            >
              {getProductTypeArrayById(products, formik.values.productId).map((productTypes) => (
                <MenuItem
                  key={productTypes}
                  value={productTypes}
                >
                  {productTypes}
                </MenuItem>
              ))}
            </InputField>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            {mandatoryFieldName('Brand', Boolean(formik.touched.brandId && formik.errors.brandId))}
            <Autocomplete
              freeSolo
              options={brands}
              onChange={(event, newValue) => {
                const getItem = newValue as BrandModel;
                formik.setFieldValue('brandId', getItem?.id);
              }}
              getOptionLabel={(option) => (option?.name ? option?.name : '')}
              selectOnFocus
              onBlur={formik.handleBlur}
              disableClearable
              renderOption={(propss, option) => <li {...propss}>{option?.name}</li>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(formik.touched.brandId && formik.errors.brandId)}
                  helperText={formik.touched.brandId && formik.errors.brandId}
                  name="brandId"
                  required
                  placeholder="Enter Brand"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              variant="button"
            >
              Model Number
            </Typography>
            <Autocomplete
              freeSolo
              value={formik.values.modelNo}
              onChange={(event, newValue) => {
                formik.setFieldValue('modelNo', newValue);
              }}
              disableClearable
              placeholder="Enter Model Number"
              options={modelNumbers.map((option) => option)}
              sx={{ mb: 1 }}
              disabled={formik.values.productId === '' || formik.values.brandId === ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={(event) => {
                    formik.setFieldValue('modelNo', event.target.value);
                  }}
                  placeholder="Enter Model Number"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <InputField
              error={Boolean(formik.touched.serialNo && formik.errors.serialNo)}
              helperText={formik.touched.serialNo && formik.errors.serialNo}
              fullWidth
              label="Serial Number"
              name="serialNo"
              onChange={(event) => {
                if (event.target.value !== ' ') {
                  formik.setFieldValue('serialNo', event.target.value);
                }
              }}
              onBlur={formik.handleBlur}
              placeholder="Enter Serial Number"
              value={formik.values.serialNo}
            />
          </Grid>
          {/* <Grid
            item
            xs={12}
            md={4}
          >
            <InputField
              fullWidth
              label="Warranty Status"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              name="warrantyStatus"
              select
              required
              value={formik.values.warrantyStatus}
            >
              {choice?.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                >
                  {option}
                </MenuItem>
              ))}
            </InputField>
          </Grid> */}
          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              sx={{ m: -1, pl: 1, mt: 0, mb: 0, fontSize: '15px', fontWeight: 500 }}
            >
              Date of Purchase
            </Typography>
            <DateField
              // disabled={formik.values.warrantyStatus === Choice.NO}
              error={Boolean(formik.errors.purchaseDate)}
              fullWidth
              helperText={formik.errors.purchaseDate}
              name="purchaseDate"
              onChange={(date) => formik.setFieldValue('purchaseDate', date)}
              value={formik.values.purchaseDate}
              maxDate={new Date()}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'right'
        }}
      >
        <Button
          color="primary"
          onClick={(e) => closeDialog(e, '')}
          variant="text"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={formik.isSubmitting || !(formik.isValid && formik.dirty)}
          onClick={() => { formik.handleSubmit(); }}
          variant="contained"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddProduct.defaultProps = {
  open: false
};

AddProduct.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  userId: PropTypes.string,
  setProductState: PropTypes.func,
};
